import { useCallback, useEffect, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { Monitor, AlertTriangle, Loader2 } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { useDisplayStore } from '@/app/store/display'
import { useAuthStore } from '@/app/store/auth'
import { displaysApi } from '../api'

/**
 * The page a command-and-control screen is pointed at.
 *
 * ENROLMENT HAPPENS ONCE, AT THE DEVICE. An admin creates the display in the admin screen,
 * copies the token, and pastes it here once on the machine that drives the wall. The server
 * then stores it as an httpOnly, path-scoped cookie — so from that moment this page holds a
 * credential it cannot read, and neither can anything injected into it. Every later start
 * sends no token at all.
 *
 * THE TOKEN IS NEVER IN THE URL. A token in a URL is a credential in browser history, in the
 * address bar over someone's shoulder, and in any photo of the wall.
 *
 * THE SESSION RENEWS ITSELF. The access token is deliberately short-lived; this page mints a
 * fresh one from the cookie well before it lapses. A screen left up for a month never has a
 * human touch it, and never holds a long-lived bearer token in reachable storage either.
 */

// Re-mint comfortably inside the server's 12h session, so a screen never shows an auth error.
const RENEW_EVERY_MS = 6 * 60 * 60 * 1000
// If the network is down when we try, retry gently rather than hammering.
const RETRY_MS = 60 * 1000

export function DisplayRunnerPage() {
    const { t } = useTranslation()
    const navigate = useNavigate()
    const isDisplay = useDisplayStore((s) => s.isDisplay)
    const setSession = useDisplayStore((s) => s.setSession)
    const clear = useDisplayStore((s) => s.clear)

    const [entry, setEntry] = useState('')
    const [error, setError] = useState<string | null>(null)
    const [busy, setBusy] = useState(false)
    // Until the first attempt returns we do not know whether this browser is enrolled —
    // showing the token field before then would flash a login prompt onto a wall.
    const [checking, setChecking] = useState(true)
    const timer = useRef<ReturnType<typeof setTimeout> | null>(null)

    const start = useCallback(async (token: string | undefined, thenGo: boolean) => {
        setBusy(true)
        setError(null)
        try {
            const { accessToken, display } = await displaysApi.startSession(token)
            // The display session lives in the auth store so every existing API call just
            // works; nothing downstream needs to know it is a display.
            useAuthStore.setState({
                isAuthenticated: true,
                accessToken,
                tokenExpiry: Date.now() + 12 * 60 * 60 * 1000,
                user: { id: -1, name: display.name, email: '', role: 'viewer' } as never,
            })
            setSession({ name: display.name, layout: display.layout || [] })
            if (timer.current) clearTimeout(timer.current)
            timer.current = setTimeout(() => { void start(undefined, false) }, RENEW_EVERY_MS)
            if (thenGo) {
                const first = (display.layout || [])[0]
                navigate(first?.path || '/recover-requests', { replace: true })
            }
            return true
        } catch {
            // A revoked or expired display must not keep a session alive on the wall, and must
            // not keep claiming to be a display: the server has already cleared its cookie.
            useAuthStore.getState().logout?.()
            clear()
            return false
        } finally {
            setBusy(false)
        }
    }, [navigate, setSession, clear])

    // On mount, try the cookie. This is the unattended-reboot path: no human, no token typed.
    useEffect(() => {
        let cancelled = false
        void (async () => {
            const ok = await start(undefined, true)
            if (cancelled) return
            // Only complain if this browser thought it was already enrolled. A fresh machine
            // simply gets the enrolment field, which is not an error.
            if (!ok && isDisplay) {
                setError(t('displays.notAuthorised', 'This display is not authorised. It may have been revoked or expired.'))
            }
            setChecking(false)
        })()
        return () => { cancelled = true; if (timer.current) clearTimeout(timer.current) }
        // Deliberately once, on mount: this is the boot path, not a reaction to state.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    const submit = async (e: React.FormEvent) => {
        e.preventDefault()
        const v = entry.trim()
        if (!v) return
        const ok = await start(v, true)
        if (!ok) {
            setError(t('displays.notAuthorised', 'This display is not authorised. It may have been revoked or expired.'))
            // Retry quietly in case the failure was the network rather than the token.
            if (timer.current) clearTimeout(timer.current)
            timer.current = setTimeout(() => { void start(undefined, false) }, RETRY_MS)
        }
    }

    if (checking) {
        return (
            <div className="min-h-screen flex flex-col items-center justify-center gap-4">
                <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
                <p className="text-sm text-muted-foreground">{t('displays.starting', 'Starting display…')}</p>
            </div>
        )
    }

    return (
        <div className="min-h-screen flex items-center justify-center p-8">
            <form onSubmit={submit} className="w-full max-w-sm space-y-4 text-center">
                <Monitor className="h-10 w-10 mx-auto text-muted-foreground" />
                <div>
                    <h1 className="text-lg font-semibold">{t('displays.enrolTitle', 'Set up this screen')}</h1>
                    <p className="text-sm text-muted-foreground mt-1">
                        {t('displays.enrolHelp', 'Paste the display token from Settings → Displays. You only do this once on this machine.')}
                    </p>
                </div>
                <Input
                    type="password"
                    autoFocus
                    value={entry}
                    onChange={(e) => setEntry(e.target.value)}
                    placeholder={t('displays.tokenPlaceholder', 'Display token')}
                    className="text-center font-mono"
                />
                {error && (
                    <p className="text-sm text-red-600 flex items-center justify-center gap-1.5">
                        <AlertTriangle className="h-4 w-4" />{error}
                    </p>
                )}
                <Button type="submit" className="w-full" disabled={busy || !entry.trim()}>
                    {busy ? t('displays.starting', 'Starting display…') : t('displays.startDisplay', 'Start display')}
                </Button>
            </form>
        </div>
    )
}
